import {Icon, Select} from 'bonprix-design-system';
import React, {useEffect, useState} from 'react';
import RichText from '@service/scripts/components/contentful/RichText.jsx';
import {AdditionalFields} from '@service/scripts/components/contentful/contactform_v2/AdditionalFields.jsx';

export function SubjectFormField({formField, formValues, setFormValues, formErrors, handleChange, setIsSubmitEnabled}) {
    const [selectedSubject, setSelectedSubject] = useState();
    const [selectedConcern, setSelectedConcern] = useState();
    const [previousConcern, setPreviousConcern] = useState();
    const [additionalFieldsGroups, setAdditionalFieldsGroups] = useState([]);

    useEffect(() => {
        // Remove values of previous additional fields from formValues
        if (previousConcern) {
            const previousAdditionalFields = previousConcern?.additionalFields ?? [];
            const updatedFormValues = {...formValues};

            previousAdditionalFields.forEach((field) => {
                if (field && field.name in updatedFormValues) {
                    delete updatedFormValues[field.name];
                }
            });
            setFormValues(updatedFormValues);
        }
        setFormValues({...formValues, concern: selectedConcern?.id});
        setAdditionalFieldsGroups([selectedConcern?.additionalFields] ?? []);
    }, [selectedConcern]);

    return (
        <>
            <div className="form-row">
                <Select
                    label={formField.labelSubject}
                    required
                    value={formValues['subject'] ?? ''}
                    name="subject"
                    onValueChange={(event) => {
                        handleChange(event, 'subject');
                        const currentSubject = formField?.subjects?.find((s) => s.id === event);
                        setSelectedSubject(currentSubject);
                        setPreviousConcern(selectedConcern);
                        setSelectedConcern(currentSubject?.concerns?.length === 1 ? currentSubject?.concerns[0] : null);
                    }}
                    error={formErrors && !!formErrors['subject']}
                    errorText={formErrors && formErrors['subject'] && formField.errorText}
                >
                    {formField.subjects.map((subject, index) => (
                        <Select.Item key={`${subject.label}_${index}`} value={subject.id}>
                            {subject.label}
                        </Select.Item>
                    ))}
                </Select>
            </div>

            <ConcernFormField
                formField={formField}
                formValues={formValues}
                formErrors={formErrors}
                handleChange={handleChange}
                setIsSubmitEnabled={setIsSubmitEnabled}
                setFormValues={setFormValues}
                selectedSubject={selectedSubject}
                selectedConcern={selectedConcern}
                setSelectedConcern={setSelectedConcern}
                previousConcern={previousConcern}
                setPreviousConcern={setPreviousConcern}
                additionalFieldsGroups={additionalFieldsGroups}
                setAdditionalFieldsGroups={setAdditionalFieldsGroups}
            />
        </>
    );
}

function ConcernFormField({
    formField,
    formValues,
    formErrors,
    handleChange,
    setIsSubmitEnabled,
    setFormValues,
    selectedSubject,
    selectedConcern,
    setSelectedConcern,
    additionalFieldsGroups,
    setAdditionalFieldsGroups,
    setPreviousConcern,
}) {
    useEffect(() => {
        if (!selectedSubject) {
            return;
        }

        const concerns = selectedSubject?.concerns ?? [];
        if (concerns.length !== 1) {
            return;
        }

        const onlyConcern = concerns[0];
        if (!onlyConcern) {
            return;
        }

        // Avoid unnecessary state updates/loops.
        if (selectedConcern?.id === onlyConcern.id && formValues?.concern === onlyConcern.id) {
            return;
        }

        setPreviousConcern(selectedConcern);
        setSelectedConcern(onlyConcern);
        setIsSubmitEnabled(onlyConcern?.submitEnabled);
        // Keep the form value in sync even if user didn't interact with the select.
        setFormValues({...formValues, concern: onlyConcern.id});
    }, [selectedSubject]);

    if (selectedSubject) {
        return (
            <>
                <div className="form-row">
                    <Select
                        label={formField.labelConcern}
                        required
                        value={selectedConcern?.id ?? ''}
                        name="concern"
                        onValueChange={(event) => {
                            handleChange(event, 'concern');
                            const concern = selectedSubject?.concerns.find((c) => c.id === event);
                            setIsSubmitEnabled(concern?.submitEnabled);
                            setSelectedConcern(concern);
                            setPreviousConcern(selectedConcern);
                        }}
                        error={formErrors && !!formErrors['concern']}
                        errorText={formErrors && formErrors['concern'] && formField.errorText}
                    >
                        {selectedSubject?.concerns?.map((concern, index) => (
                            <Select.Item key={`${concern.label}_${index}`} value={concern.id}>
                                {concern.label}
                            </Select.Item>
                        ))}
                    </Select>

                    {selectedConcern?.infoText && (
                        <div className="preflight m-0 flex justify-between items-start p-5 rounded gap-4 bg-clr-bg-info">
                            <div className="flex gap-2">
                                <Icon variant="hint" className="fill-clr-fg-info" />
                                <RichText richTextModel={selectedConcern?.infoText} />
                            </div>
                        </div>
                    )}
                </div>
                {selectedConcern && (
                    <AdditionalFields
                        parentField={selectedConcern}
                        formValues={formValues}
                        setFormValues={setFormValues}
                        formErrors={formErrors}
                        handleChange={handleChange}
                        additionalFieldsGroups={additionalFieldsGroups}
                        setAdditionalFieldsGroups={setAdditionalFieldsGroups}
                    />
                )}
            </>
        );
    }
}
